import { useCallback, useEffect, useRef, useState } from "react";
import { Subscription } from "@rails/actioncable";
import { getCableConsumer } from "./consumer";

export type CableStatus = "idle" | "connecting" | "connected" | "disconnected" | "rejected";

export interface UseCableSubscriptionOptions<T> {
  channel: string;
  params?: Record<string, unknown>;
  enabled?: boolean;
  onMessage: (data: T) => void;
  onConnected?: () => void;
  onReconnect?: () => void;
  onDisconnected?: () => void;
}

export interface UseCableSubscriptionResult {
  status: CableStatus;
  perform: (action: string, data?: object) => void;
}

function parseMessage(data: unknown): unknown {
  if (typeof data !== "string") return data;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/**
 * Subscribes to one ActionCable channel on the shared consumer. Callbacks live in refs so the
 * subscription is only recreated when the channel or its params change.
 */
export function useCableSubscription<T>(opts: UseCableSubscriptionOptions<T>): UseCableSubscriptionResult {
  const { channel, params, enabled = true } = opts;
  const [status, setStatus] = useState<CableStatus>("idle");
  const subscriptionRef = useRef<Subscription | null>(null);
  const hasConnectedRef = useRef(false);

  const callbacksRef = useRef(opts);
  useEffect(() => {
    callbacksRef.current = opts;
  });

  const paramsKey = JSON.stringify(params ?? {});

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      setStatus("idle");
      return undefined;
    }

    const identifier = { channel, ...(JSON.parse(paramsKey) as Record<string, unknown>) };
    setStatus("connecting");
    hasConnectedRef.current = false;

    const subscription = getCableConsumer().subscriptions.create(identifier, {
      connected() {
        setStatus("connected");
        if (hasConnectedRef.current) {
          callbacksRef.current.onReconnect?.();
        } else {
          hasConnectedRef.current = true;
          callbacksRef.current.onConnected?.();
        }
      },
      disconnected() {
        setStatus("disconnected");
        callbacksRef.current.onDisconnected?.();
      },
      rejected() {
        setStatus("rejected");
      },
      received(data: unknown) {
        const message = parseMessage(data);
        if (message === null || message === undefined) return;
        callbacksRef.current.onMessage(message as T);
      },
    });
    subscriptionRef.current = subscription;

    return () => {
      subscription.unsubscribe();
      if (subscriptionRef.current === subscription) subscriptionRef.current = null;
      hasConnectedRef.current = false;
    };
  }, [channel, paramsKey, enabled]);

  const perform = useCallback((action: string, data?: object) => {
    subscriptionRef.current?.perform(action, data);
  }, []);

  return { status, perform };
}
